import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { getFacilityCreateEditDialogSchemas } from '../../validation/facilityFormValidation';
import facilityService from '../../services/facilityService';
import locationService from '../../services/locationService';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import FormMultiSelect from '../../components/common/FormMultiSelect';
import {
  mapApiResponseToFormValues,
  normalizeFacilityCodeInput,
  type FacilityFormValues,
} from '../../utils/facilityMapper';
import { trimFormStringFields } from '../../utils/trimFormStringFields';
import type { Facility } from '../../types/facility.types';
import { ICON_STROKE_WIDTH } from '../../constants';
import { X } from 'lucide-react';
import './FacilityDialogs/facilityDialogs.css';
import './UserManagement.css';
import { DEFAULT_PACS_IP_ADDRESS } from '../../constants/facilityDefaults';

const FACILITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Diagnostic Center', label: 'Diagnostic Center' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'Clinic', label: 'Clinic' },
  { value: 'HUB', label: 'Hub' },
  { value: 'SPOKE', label: 'Spoke' },
];

const FACILITY_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Suspended', label: 'Suspended' },
];

const DUAL_STAGE_REPORT_APPROVAL_OPTIONS: { value: string; label: string }[] = [
  { value: 'Yes', label: 'Enabled' },
  { value: 'No', label: 'Disabled' },
];

function facilityDialogSubmitLabel(submitting: boolean, isEdit: boolean): string {
  if (submitting) {
    return 'Saving…';
  }
  if (isEdit) {
    return 'Update Facility';
  }
  return 'Create Facility';
}

function resolveFieldError(
  k: keyof FacilityFormValues,
  values: FacilityFormValues,
  errors: Record<string, unknown>,
  touched: Record<string, unknown>,
  facilityDetailsKeys: (keyof FacilityFormValues)[],
  facilityDetailsSchema: Yup.AnyObjectSchema,
  schema: Yup.AnyObjectSchema
): string | undefined {
  const fieldError = errors[k as string];
  if (!fieldError) {
    return undefined;
  }
  const fieldValue = values[k];
  const hasValue = Array.isArray(fieldValue)
    ? fieldValue.length > 0
    : fieldValue != null && fieldValue !== '';
  if (!touched[k as string] && !hasValue) {
    return undefined;
  }
  const activeSchema = facilityDetailsKeys.includes(k) ? facilityDetailsSchema : schema;
  try {
    activeSchema.validateSyncAt(k as string, values);
    return undefined;
  } catch {
    return String(fieldError);
  }
}

async function validateAndGoToAdminTab(args: {
  values: FacilityFormValues;
  touched: Record<string, boolean>;
  setTouched: (t: Record<string, boolean>, validate?: boolean) => Promise<void> | void;
  setFieldError: (field: string, message?: string) => void;
  facilityDetailsKeys: (keyof FacilityFormValues)[];
  facilityDetailsSchema: Yup.AnyObjectSchema;
  setTab: (tab: 'facility' | 'admin') => void;
}): Promise<void> {
  const touchFacility = args.facilityDetailsKeys.reduce(
    (acc, key) => ({ ...acc, [key]: true }),
    {} as Record<string, boolean>
  );
  await args.setTouched({ ...args.touched, ...touchFacility }, false);
  try {
    await args.facilityDetailsSchema.validate(args.values, { abortEarly: false });
    args.setTab('admin');
  } catch (e: unknown) {
    if (!(e instanceof Yup.ValidationError)) {
      return;
    }
    const pathErrors: Partial<Record<keyof FacilityFormValues, string>> = {};
    const inner = e.inner.length ? e.inner : [e];
    for (const ve of inner) {
      if (ve.path) {
        pathErrors[ve.path as keyof FacilityFormValues] = ve.message;
      }
    }
    for (const key of args.facilityDetailsKeys) {
      args.setFieldError(key as string, pathErrors[key]);
    }
  }
}

const APPROVED_BY_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Senior Radiologist', label: 'Senior Radiologist' },
  { value: 'QC Technician', label: 'QC Technician' },
];

type TabKey = 'facility' | 'admin';

function FacilityCreateEditDialogCloseButton({
  disabled,
  onClose,
}: Readonly<{
  disabled?: boolean;
  onClose: () => void;
}>) {
  return (
    <button
      type="button"
      className="facility-create-edit-dialog-close-btn mr-4"
      onClick={() => !disabled && onClose()}
      disabled={disabled}
      aria-label="Close dialog"
    >
      <X
        className="h-5 w-5"
        strokeWidth={ICON_STROKE_WIDTH}
      />
    </button>
  );
}

/** Fields validated before the Admin User tab is enabled or before navigating with Next. */
const FACILITY_DETAILS_KEYS: (keyof FacilityFormValues)[] = [
  'name',
  'code',
  'type',
  'description',
  'status',
  'hasRadiologistHierarchy',
  'seniorRadiologistReviewRequired',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'country',
  'pincode',
  'phoneNo',
  'emailId',
];

export type FacilityDialogMode = 'create' | 'edit';

interface FacilityDialogCityFetcherProps {
  dialogOpen: boolean;
  setCityOptions: (cities: { value: string; label: string }[]) => void;
  setIsLoadingCities: (loading: boolean) => void;
  skipNextCityFetchRef: React.MutableRefObject<boolean>;
}

/** Load city list when Indian state changes (same pattern as AddFacility). */
function FacilityDialogCityFetcher({
  dialogOpen,
  setCityOptions,
  setIsLoadingCities,
  skipNextCityFetchRef,
}: Readonly<FacilityDialogCityFetcherProps>) {
  const { values, setFieldValue } = useFormikContext<FacilityFormValues>();
  const previousStateRef = useRef<string | undefined>(undefined);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!dialogOpen) {
      hasInitializedRef.current = false;
      previousStateRef.current = undefined;
      setIsLoadingCities(false);
      return;
    }

    if (!values.state) {
      hasInitializedRef.current = false;
      if (previousStateRef.current) {
        previousStateRef.current = '';
        setCityOptions([]);
      } else {
        previousStateRef.current = '';
      }
      return;
    }

    if (skipNextCityFetchRef.current) {
      skipNextCityFetchRef.current = false;
      hasInitializedRef.current = true;
      previousStateRef.current = values.state;
      return;
    }

    if (previousStateRef.current === values.state && hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    previousStateRef.current = values.state;

    let cancelled = false;
    setIsLoadingCities(true);
    void locationService
      .getCities(values.state)
      .then(async cities => {
        if (cancelled) {
          return;
        }
        setCityOptions(cities);
        const currentCity = values.city;
        if (currentCity && !cities.some(c => c.value === currentCity)) {
          const byLabel = cities.find(
            c => String(c.label || '').toLowerCase() === String(currentCity).trim().toLowerCase()
          );
          await setFieldValue('city', byLabel ? byLabel.value : '', false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCityOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingCities(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    dialogOpen,
    values.state,
    values.city,
    setCityOptions,
    setIsLoadingCities,
    setFieldValue,
    skipNextCityFetchRef,
  ]);

  return null;
}

function FacilityDialogPincodeLookup({
  dialogOpen,
  stateOptions,
  setCityOptions,
  setIsLoadingCities,
  setIsLoadingPincode,
  skipNextCityFetchRef,
}: Readonly<{
  dialogOpen: boolean;
  stateOptions: { value: string; label: string }[];
  setCityOptions: (cities: { value: string; label: string }[]) => void;
  setIsLoadingCities: (loading: boolean) => void;
  setIsLoadingPincode: (loading: boolean) => void;
  skipNextCityFetchRef: React.MutableRefObject<boolean>;
}>) {
  const { values, setFieldValue } = useFormikContext<FacilityFormValues>();
  const lastLookupRef = useRef('');
  const stateOptionsRef = useRef(stateOptions);
  stateOptionsRef.current = stateOptions;

  useEffect(() => {
    if (!dialogOpen) {
      lastLookupRef.current = '';
      setIsLoadingPincode(false);
      return;
    }

    const pin = String(values.pincode || '').replaceAll(/\D/g, '');
    if (pin.length !== 6) {
      lastLookupRef.current = '';
      skipNextCityFetchRef.current = false;
      setIsLoadingPincode(false);
      return;
    }
    if (pin === lastLookupRef.current) {
      return;
    }
    lastLookupRef.current = pin;

    let cancelled = false;
    setIsLoadingPincode(true);
    void locationService
      .populateAddressFromPincode(pin, stateOptionsRef.current, {
        setFieldValue: (field, value, shouldValidate) => {
          if (cancelled) {
            return;
          }
          setFieldValue(field, value, shouldValidate).catch(() => undefined);
        },
        setCityOptions: options => {
          if (!cancelled) {
            setCityOptions(options);
          }
        },
        setIsLoadingCities: loading => {
          if (!cancelled) {
            setIsLoadingCities(loading);
          }
        },
        skipNextCityFetch: skipNextCityFetchRef,
        isActive: () => !cancelled,
      })
      .catch(() => false)
      .finally(() => {
        if (!cancelled) {
          setIsLoadingPincode(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    dialogOpen,
    values.pincode,
    setFieldValue,
    setCityOptions,
    setIsLoadingCities,
    setIsLoadingPincode,
    skipNextCityFetchRef,
  ]);

  return null;
}

function FacilityDialogLocationMatch({
  stateOptions,
  cityOptions,
  dialogOpen,
}: {
  stateOptions: { value: string; label: string }[];
  cityOptions: { value: string; label: string }[];
  dialogOpen: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<FacilityFormValues>();

  useEffect(() => {
    if (!dialogOpen || !stateOptions.length) {
      return;
    }
    const raw = String(values.state || '').trim();
    if (!raw) {
      return;
    }
    if (stateOptions.some(o => o.value === raw)) {
      return;
    }
    const byLabel = stateOptions.find(
      o => String(o.label || '').toLowerCase() === raw.toLowerCase()
    );
    if (byLabel) {
      void setFieldValue('state', byLabel.value);
    }
  }, [dialogOpen, stateOptions, values.state, setFieldValue]);

  useEffect(() => {
    if (!dialogOpen || !cityOptions.length) {
      return;
    }
    const raw = String(values.city || '').trim();
    if (!raw) {
      return;
    }
    if (cityOptions.some(o => o.value === raw)) {
      return;
    }
    const byLabel = cityOptions.find(
      o => String(o.label || '').toLowerCase() === raw.toLowerCase()
    );
    if (byLabel) {
      void setFieldValue('city', byLabel.value);
    }
  }, [dialogOpen, cityOptions, values.city, setFieldValue]);

  return null;
}

function FacilityLinkedHubSelect({
  value,
  onChange,
  error,
  hubFacilityOptions,
  isLoadingHubFacilities,
  setHubFacilityOptions,
  setIsLoadingHubFacilities,
  dialogOpen,
  excludeFacilityId,
}: Readonly<{
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
  hubFacilityOptions: { value: string; label: string }[];
  isLoadingHubFacilities: boolean;
  setHubFacilityOptions: (options: { value: string; label: string }[]) => void;
  setIsLoadingHubFacilities: (loading: boolean) => void;
  dialogOpen: boolean;
  /** When editing, omit this facility from hub options (cannot link to itself). */
  excludeFacilityId?: number | string;
}>) {
  const excludeId = excludeFacilityId != null ? String(excludeFacilityId) : '';

  useEffect(() => {
    if (!excludeId || !value.length) {
      return;
    }
    const filtered = value.filter(id => id !== excludeId);
    if (filtered.length !== value.length) {
      onChange(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prune self once when excludeId/value change
  }, [excludeId, value.join(',')]);

  useEffect(() => {
    if (!dialogOpen) {
      setHubFacilityOptions([]);
      return;
    }
    let cancelled = false;
    setIsLoadingHubFacilities(true);
    facilityService
      .getFacilitiesByType('HUB', 1, 50)
      .then(res => {
        if (cancelled) {
          return;
        }
        const hubs = res.success && res.data ? res.data : [];
        setHubFacilityOptions(
          hubs
            .filter(h => h.id != null && String(h.id) !== excludeId)
            .map(h => ({
              value: String(h.id),
              label: `${h.facility_name || 'Unnamed'} (${h.facility_code || h.id})`,
            }))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setHubFacilityOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingHubFacilities(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, excludeId, setHubFacilityOptions, setIsLoadingHubFacilities]);

  return (
    <FormMultiSelect
      label="Linked Hub Facilities"
      options={hubFacilityOptions}
      value={value}
      onChange={onChange}
      placeholder={isLoadingHubFacilities ? 'Loading hubs…' : 'Select hub facilities'}
      error={error}
      disabled={isLoadingHubFacilities}
    />
  );
}

function FieldError({ message }: Readonly<{ message?: string }>) {
  if (!message) {
    return null;
  }
  return <div className="facility-dialog-error">{message}</div>;
}

function SpokeLinkedHubFields(
  props: Readonly<{
    values: FacilityFormValues;
    err: (k: keyof FacilityFormValues) => string | undefined;
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<unknown>;
    hubFacilityOptions: { value: string; label: string }[];
    setHubFacilityOptions: (options: { value: string; label: string }[]) => void;
    isLoadingHubFacilities: boolean;
    setIsLoadingHubFacilities: (loading: boolean) => void;
    open: boolean;
    isEdit: boolean;
    facilityId?: number | string;
  }>
) {
  if (props.values.type !== 'SPOKE') {
    return null;
  }
  return (
    <div
      className="facility-dialog-grid-1"
      style={{ marginTop: 16 }}
    >
      <FacilityLinkedHubSelect
        value={props.values.linkedHubFacilityIds ?? []}
        onChange={ids => {
          props.setFieldValue('linkedHubFacilityIds', ids).catch(() => undefined);
        }}
        error={props.err('linkedHubFacilityIds')}
        hubFacilityOptions={props.hubFacilityOptions}
        isLoadingHubFacilities={props.isLoadingHubFacilities}
        setHubFacilityOptions={props.setHubFacilityOptions}
        setIsLoadingHubFacilities={props.setIsLoadingHubFacilities}
        dialogOpen={props.open}
        excludeFacilityId={props.isEdit ? props.facilityId : undefined}
      />
    </div>
  );
}

function DualStageApprovedByFields(
  props: Readonly<{
    values: FacilityFormValues;
    handleChange: React.ChangeEventHandler<any>;
    err: (k: keyof FacilityFormValues) => string | undefined;
  }>
) {
  if (props.values.hasRadiologistHierarchy !== 'Yes') {
    return null;
  }
  return (
    <div
      className="facility-dialog-grid-2"
      style={{ marginTop: 16 }}
    >
      <div className="facility-dialog-field">
        <FormSelectVirtualized
          label="Approved by"
          name="seniorRadiologistReviewRequired"
          required
          value={
            APPROVED_BY_ROLE_OPTIONS.some(
              o => o.value === props.values.seniorRadiologistReviewRequired
            )
              ? props.values.seniorRadiologistReviewRequired
              : 'Senior Radiologist'
          }
          onChange={props.handleChange}
          options={APPROVED_BY_ROLE_OPTIONS}
          placeholder="Select"
          error={props.err('seniorRadiologistReviewRequired')}
        />
      </div>
    </div>
  );
}

function FacilityDetailsTabFields(
  props: Readonly<{
    values: FacilityFormValues;
    handleChange: React.ChangeEventHandler<any>;
    trimBlur: (
      field: keyof FacilityFormValues
    ) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    err: (k: keyof FacilityFormValues) => string | undefined;
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<unknown>;
    setFieldTouched: (
      field: string,
      touched?: boolean,
      shouldValidate?: boolean
    ) => Promise<unknown>;
    stateOptions: { value: string; label: string }[];
    cityOptions: { value: string; label: string }[];
    isLoadingStates: boolean;
    isLoadingCities: boolean;
    hubFacilityOptions: { value: string; label: string }[];
    setHubFacilityOptions: (options: { value: string; label: string }[]) => void;
    isLoadingHubFacilities: boolean;
    setIsLoadingHubFacilities: (loading: boolean) => void;
    isLoadingPincode: boolean;
    isEdit: boolean;
    facilityId?: number | string;
    open: boolean;
  }>
): JSX.Element {
  const {
    values,
    handleChange,
    trimBlur,
    err,
    setFieldValue,
    setFieldTouched,
    stateOptions,
    cityOptions,
    isLoadingStates,
    isLoadingCities,
    hubFacilityOptions,
    setHubFacilityOptions,
    isLoadingHubFacilities,
    setIsLoadingHubFacilities,
    isLoadingPincode,
    isEdit,
    facilityId,
    open,
  } = props;
  return (
    <>
      <div className="facility-dialog-section">
        <h3 className="facility-dialog-section-title">Basic Information</h3>
        <div className="facility-dialog-grid-1">
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Facility Name <span className="facility-dialog-required">*</span>
            </label>
            <input
              className="facility-dialog-input"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={trimBlur('name')}
              placeholder="Enter facility name"
            />
            <FieldError message={err('name')} />
          </div>
        </div>

        <div
          className="facility-dialog-grid-2"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Facility Code <span className="facility-dialog-required">*</span>
            </label>
            <input
              className="facility-dialog-input"
              name="code"
              value={values.code}
              onChange={e => {
                setFieldValue('code', normalizeFacilityCodeInput(e.target.value)).catch(
                  () => undefined
                );
              }}
              placeholder="e.g. APL001"
              maxLength={20}
              onBlur={trimBlur('code')}
            />
            <FieldError message={err('code')} />
          </div>
          <div className="facility-dialog-field">
            <FormSelectVirtualized
              label="Facility Type"
              name="type"
              required
              value={values.type}
              onChange={e => {
                const nextType = e.target.value;
                setFieldValue('type', nextType, true).catch(() => undefined);
                setFieldTouched('type', true, false).catch(() => undefined);
                if (nextType !== 'SPOKE') {
                  setFieldValue('linkedHubFacilityIds', [], false).catch(() => undefined);
                } else if (facilityId != null) {
                  const selfId = String(facilityId);
                  const linked = values.linkedHubFacilityIds ?? [];
                  if (linked.includes(selfId)) {
                    setFieldValue(
                      'linkedHubFacilityIds',
                      linked.filter(id => id !== selfId),
                      false
                    ).catch(() => undefined);
                  }
                }
              }}
              options={FACILITY_TYPE_OPTIONS}
              placeholder="Select type"
              error={err('type')}
            />
          </div>
        </div>

        <SpokeLinkedHubFields
          values={values}
          err={err}
          setFieldValue={setFieldValue}
          hubFacilityOptions={hubFacilityOptions}
          setHubFacilityOptions={setHubFacilityOptions}
          isLoadingHubFacilities={isLoadingHubFacilities}
          setIsLoadingHubFacilities={setIsLoadingHubFacilities}
          open={open}
          isEdit={isEdit}
          facilityId={facilityId}
        />

        <div
          className="facility-dialog-grid-1"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">Facility Description</label>
            <textarea
              className="facility-dialog-textarea"
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={trimBlur('description')}
              placeholder="Enter Facility Description"
              maxLength={250}
            />
            <FieldError message={err('description')} />
          </div>
        </div>

        <div
          className="facility-dialog-grid-2"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <FormSelectVirtualized
              label="Status"
              name="status"
              required
              value={values.status}
              onChange={handleChange}
              options={FACILITY_STATUS_OPTIONS}
              placeholder="Select status"
              error={err('status')}
            />
          </div>
          <div className="facility-dialog-field">
            <FormSelectVirtualized
              label="Dual stage report approval"
              name="hasRadiologistHierarchy"
              required
              value={values.hasRadiologistHierarchy ?? 'No'}
              onChange={e => {
                handleChange(e);
                if (e.target.value === 'No') {
                  setFieldValue('seniorRadiologistReviewRequired', 'Senior Radiologist').catch(
                    () => undefined
                  );
                }
              }}
              options={DUAL_STAGE_REPORT_APPROVAL_OPTIONS}
              placeholder="Select"
              error={err('hasRadiologistHierarchy')}
            />
          </div>
        </div>

        <DualStageApprovedByFields
          values={values}
          handleChange={handleChange}
          err={err}
        />
      </div>

      <div className="facility-dialog-section">
        <h3 className="facility-dialog-section-title">Address &amp; Contact</h3>
        <div className="facility-dialog-grid-1">
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Address Line 1 <span className="facility-dialog-required">*</span>
            </label>
            <input
              className="facility-dialog-input"
              name="addressLine1"
              value={values.addressLine1}
              onChange={handleChange}
              onBlur={trimBlur('addressLine1')}
              placeholder="Street or building address"
            />
            <FieldError message={err('addressLine1')} />
          </div>
        </div>

        <div
          className="facility-dialog-grid-1"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">Address Line 2</label>
            <input
              className="facility-dialog-input"
              name="addressLine2"
              value={values.addressLine2}
              onChange={handleChange}
              onBlur={trimBlur('addressLine2')}
              placeholder="Additional location info"
            />
          </div>
        </div>

        <div
          className="facility-dialog-grid-2"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <FormSelectVirtualized
              label="State"
              name="state"
              required
              value={values.state}
              onChange={handleChange}
              options={stateOptions}
              placeholder={isLoadingStates ? 'Loading...' : 'Select state'}
              disabled={isLoadingStates || isLoadingPincode}
              error={err('state')}
            />
          </div>
          <div className="facility-dialog-field">
            <FormSelectVirtualized
              label="City"
              name="city"
              required
              value={values.city}
              onChange={handleChange}
              options={cityOptions}
              placeholder={
                values.state && (isLoadingCities || isLoadingPincode)
                  ? 'Loading...'
                  : values.state
                    ? 'Select city'
                    : 'Select state first'
              }
              disabled={!values.state || isLoadingCities || isLoadingPincode}
              error={err('city')}
            />
          </div>
        </div>

        <div
          className="facility-dialog-grid-2"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Country <span className="facility-dialog-required">*</span>
            </label>
            <select
              className="facility-dialog-select"
              name="country"
              value="India"
              disabled
              aria-readonly
            >
              <option value="India">India</option>
            </select>
          </div>
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Pincode <span className="facility-dialog-required">*</span>
            </label>
            <input
              className="facility-dialog-input"
              name="pincode"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="postal-code"
              maxLength={6}
              value={values.pincode}
              onChange={e => {
                const digits = e.target.value.replaceAll(/\D/g, '').slice(0, 6);
                void setFieldValue('pincode', digits, false);
              }}
              onBlur={trimBlur('pincode')}
              placeholder="6-digit pincode"
            />
            <FieldError message={err('pincode')} />
          </div>
        </div>

        <div
          className="facility-dialog-grid-2"
          style={{ marginTop: 16 }}
        >
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Contact Number <span className="facility-dialog-required">*</span>
            </label>
            <div
              className={[
                'create-user-dialog-mobile-wrap',
                err('phoneNo') ? 'create-user-dialog-input-error' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className="create-user-dialog-mobile-prefix"
                aria-hidden
              >
                +91
              </span>
              <input
                className="create-user-dialog-input facility-dialog-input"
                name="phoneNo"
                value={values.phoneNo}
                onChange={e => {
                  const digits = e.target.value.replaceAll(/\D/g, '').slice(0, 10);
                  setFieldValue('phoneNo', digits).catch(() => undefined);
                }}
                placeholder="xxxxxxxxxx"
                maxLength={10}
                inputMode="numeric"
                autoComplete="tel"
                aria-label="Contact number (10 digits)"
                aria-invalid={Boolean(err('phoneNo'))}
                onBlur={trimBlur('phoneNo')}
              />
            </div>
            <FieldError message={err('phoneNo')} />
          </div>
          <div className="facility-dialog-field">
            <label className="facility-dialog-label">
              Email ID <span className="facility-dialog-required">*</span>
            </label>
            <input
              className="facility-dialog-input"
              type="email"
              name="emailId"
              value={values.emailId}
              onChange={handleChange}
              onBlur={trimBlur('emailId')}
              placeholder="facility@example.com"
            />
            <FieldError message={err('emailId')} />
          </div>
        </div>
      </div>
    </>
  );
}

function AdminUserTabFields(
  props: Readonly<{
    values: FacilityFormValues;
    handleChange: React.ChangeEventHandler<any>;
    trimBlur: (
      field: keyof FacilityFormValues
    ) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    err: (k: keyof FacilityFormValues) => string | undefined;
    isEdit: boolean;
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<unknown>;
  }>
): JSX.Element {
  const { values, handleChange, trimBlur, err, isEdit, setFieldValue } = props;
  return (
    <div className="facility-dialog-section">
      <div className="facility-dialog-grid-2">
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Employee ID <span className="facility-dialog-required">*</span>
          </label>
          <input
            className="facility-dialog-input"
            name="employeeId"
            value={values.employeeId}
            onChange={handleChange}
            onBlur={trimBlur('employeeId')}
            placeholder="e.g. EMP-001"
          />
          <FieldError message={err('employeeId')} />
        </div>
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Full Name <span className="facility-dialog-required">*</span>
          </label>
          <input
            className="facility-dialog-input"
            name="fullName"
            value={values.fullName}
            onChange={handleChange}
            onBlur={trimBlur('fullName')}
            placeholder="Enter full name"
          />
          <FieldError message={err('fullName')} />
        </div>
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Phone No. <span className="facility-dialog-required">*</span>
          </label>
          <div
            className={[
              'create-user-dialog-mobile-wrap',
              err('adminPhoneNo') ? 'create-user-dialog-input-error' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span
              className="create-user-dialog-mobile-prefix"
              aria-hidden
            >
              +91
            </span>
            <input
              className="create-user-dialog-input facility-dialog-input"
              name="adminPhoneNo"
              value={values.adminPhoneNo}
              onChange={e => {
                const digits = e.target.value.replaceAll(/\D/g, '').slice(0, 10);
                setFieldValue('adminPhoneNo', digits).catch(() => undefined);
              }}
              placeholder="xxxxxxxxxx"
              maxLength={10}
              inputMode="numeric"
              autoComplete="tel"
              aria-label="Admin phone number (10 digits)"
              aria-invalid={Boolean(err('adminPhoneNo'))}
              onBlur={trimBlur('adminPhoneNo')}
            />
          </div>
          <FieldError message={err('adminPhoneNo')} />
        </div>
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Email ID <span className="facility-dialog-required">*</span>
          </label>
          <input
            className="facility-dialog-input"
            type="email"
            name="adminEmailId"
            value={values.adminEmailId}
            onChange={handleChange}
            onBlur={trimBlur('adminEmailId')}
            placeholder="Enter email address"
          />
          <FieldError message={err('adminEmailId')} />
        </div>
      </div>

      <div
        className="facility-dialog-field"
        style={{ marginTop: 16 }}
      >
        <label className="facility-dialog-label">
          Gender <span className="facility-dialog-required">*</span>
        </label>
        <div className="facility-dialog-gender-row">
          {(['Male', 'Female', 'Other'] as const).map(g => (
            <button
              key={g}
              type="button"
              className={`facility-dialog-gender-btn ${values.gender === g ? 'facility-dialog-gender-btn--active' : ''}`}
              onClick={() => setFieldValue('gender', g)}
            >
              {g}
            </button>
          ))}
        </div>
        <FieldError message={err('gender')} />
      </div>

      <div
        className="facility-dialog-grid-2"
        style={{ marginTop: 16 }}
      >
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Username <span className="facility-dialog-required">*</span>
          </label>
          <input
            className="facility-dialog-input"
            name="username"
            value={values.username}
            onChange={handleChange}
            onBlur={trimBlur('username')}
            placeholder="Enter username"
          />
          <FieldError message={err('username')} />
        </div>
        <div className="facility-dialog-field">
          <label className="facility-dialog-label">
            Password {!isEdit ? <span className="facility-dialog-required">*</span> : null}
          </label>
          <input
            className="facility-dialog-input"
            name="password"
            type="password"
            value={values.password}
            onChange={handleChange}
            onBlur={trimBlur('password')}
            placeholder="Enter password (min 8 characters)"
          />
          <FieldError message={err('password')} />
        </div>
      </div>
    </div>
  );
}

function FacilityDialogLoadingShell({
  isEdit,
  submitting,
  onClose,
}: Readonly<{
  isEdit: boolean;
  submitting: boolean;
  onClose: () => void;
}>) {
  return (
    <>
      <div className="facility-create-edit-dialog-header-top-row">
        <DialogHeader className="facility-create-edit-dialog-header-title-only">
          <DialogTitle className="create-user-dialog-title">
            {isEdit ? 'Edit Facility' : 'Create New Facility'}
          </DialogTitle>
        </DialogHeader>
        <FacilityCreateEditDialogCloseButton
          disabled={submitting}
          onClose={onClose}
        />
      </div>
      <div className="create-user-dialog-body facility-dialog-body">
        <div className="facility-dialog-loading">Loading facility…</div>
      </div>
    </>
  );
}

function FacilityDialogErrorShell({
  isEdit,
  submitting,
  onClose,
}: Readonly<{
  isEdit: boolean;
  submitting: boolean;
  onClose: () => void;
}>) {
  return (
    <>
      <div className="facility-create-edit-dialog-header-top-row">
        <DialogHeader className="facility-create-edit-dialog-header-title-only">
          <DialogTitle className="create-user-dialog-title">
            {isEdit ? 'Edit Facility' : 'Create New Facility'}
          </DialogTitle>
        </DialogHeader>
        <FacilityCreateEditDialogCloseButton
          disabled={submitting}
          onClose={onClose}
        />
      </div>
      <div className="create-user-dialog-body facility-dialog-body">
        <p
          className="facility-dialog-label"
          style={{ margin: 0 }}
        >
          Could not load this facility. Try again or close the dialog.
        </p>
        <div
          className="create-user-dialog-footer facility-dialog-footer"
          style={{ marginTop: 16 }}
        >
          <button
            type="button"
            className="facility-dialog-btn facility-dialog-btn--primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function resolveFacilityDialogBody(
  showFetchSpinner: boolean,
  showFetchError: boolean,
  showForm: boolean
): 'loading' | 'error' | 'form' | 'empty' {
  if (showFetchSpinner) {
    return 'loading';
  }
  if (showFetchError) {
    return 'error';
  }
  if (showForm) {
    return 'form';
  }
  return 'empty';
}

function FacilityDialogContentSwitcher({
  mode,
  isEdit,
  submitting,
  onClose,
  formContent,
}: Readonly<{
  mode: 'loading' | 'error' | 'form' | 'empty';
  isEdit: boolean;
  submitting: boolean;
  onClose: () => void;
  formContent: React.ReactNode;
}>) {
  if (mode === 'loading') {
    return (
      <FacilityDialogLoadingShell
        isEdit={isEdit}
        submitting={submitting}
        onClose={onClose}
      />
    );
  }
  if (mode === 'error') {
    return (
      <FacilityDialogErrorShell
        isEdit={isEdit}
        submitting={submitting}
        onClose={onClose}
      />
    );
  }
  if (mode === 'form') {
    return <>{formContent}</>;
  }
  return null;
}

export default function FacilityCreateEditDialog({
  open,
  mode,
  facilityId,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  mode: FacilityDialogMode;
  facilityId?: number | string;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}) {
  const [tab, setTab] = useState<TabKey>('facility');
  const [submitting, setSubmitting] = useState(false);
  const [facilityFetchLoading, setFacilityFetchLoading] = useState(false);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [hubFacilityOptions, setHubFacilityOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [isLoadingHubFacilities, setIsLoadingHubFacilities] = useState(false);
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const skipNextCityFetchRef = useRef(false);

  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!open) {
      setStateOptions([]);
      setCityOptions([]);
      setIsLoadingStates(false);
      setIsLoadingCities(false);
      setIsLoadingPincode(false);
      skipNextCityFetchRef.current = false;
      return;
    }
    let cancelled = false;
    setIsLoadingStates(true);
    locationService
      .getStates()
      .then(states => {
        if (!cancelled) {
          setStateOptions(states);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStateOptions([]);
        }
      })
      .finally(() => {
        setIsLoadingStates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTab('facility');
      setFacility(null);
      setFacilityFetchLoading(false);
      return;
    }
    if (!isEdit || facilityId == null) {
      setFacility(null);
      setFacilityFetchLoading(false);
      return;
    }
    let mounted = true;
    setFacility(null);
    setFacilityFetchLoading(true);
    facilityService
      .getFacilityById(facilityId)
      .then(res => {
        if (!mounted) {
          return;
        }
        setFacility(res.success ? (res.data as Facility) : null);
      })
      .finally(() => {
        if (mounted) {
          setFacilityFetchLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [open, isEdit, facilityId]);

  const initialValues: FacilityFormValues = useMemo(() => {
    const base: FacilityFormValues = {
      name: '',
      code: '',
      type: '',
      description: '',
      phoneNo: '',
      emailId: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      logo: null,
      logoFileName: '',
      headerText: '',
      footerText: '',
      pacsAETitle: 'DCM4CHEE',
      pacsIPAddress: DEFAULT_PACS_IP_ADDRESS,
      pacsPort: '11112',
      integrationStatus: 'Pending',
      status: 'Active',
      employeeId: '',
      fullName: '',
      adminPhoneNo: '',
      adminEmailId: '',
      gender: '',
      username: '',
      password: '',
      designation: '',
      hasRadiologistHierarchy: 'No',
      seniorRadiologistReviewRequired: 'Senior Radiologist',
      letterheadLogoUrl: '',
      risUrl: '',
      linkedHubFacilityIds: [],
    };

    if (!facility) {
      return base;
    }
    const mapped = mapApiResponseToFormValues(facility) as Partial<FacilityFormValues>;
    return { ...base, ...mapped, logo: null, country: 'India' };
  }, [facility]);

  const { facilityDetailsSchema, schema } = useMemo(
    () => getFacilityCreateEditDialogSchemas(isEdit),
    [isEdit]
  );

  const submit = async (values: FacilityFormValues) => {
    setSubmitting(true);
    try {
      const body = trimFormStringFields(
        { ...values, country: 'India' } as Record<string, unknown>,
        ['password']
      ) as FacilityFormValues;
      const res = isEdit
        ? await facilityService.updateFacility(Number(facilityId), body)
        : await facilityService.createFacility(body);
      if (res.success) {
        onSuccess?.();
        onOpenChange(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showForm = !isEdit || (facilityId != null && facility != null && !facilityFetchLoading);
  const showFetchSpinner = isEdit && facilityId != null && facilityFetchLoading;
  const showFetchError = isEdit && facilityId != null && !facilityFetchLoading && facility == null;
  const dialogBodyMode = resolveFacilityDialogBody(showFetchSpinner, showFetchError, showForm);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      shouldCloseOnEsc={!submitting}
      /* Avoid losing in-progress facility details when the user clicks the dimmed area by mistake. */
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        showCloseButton={false}
        className="facility-dialog-content create-user-dialog-content facility-create-edit-dialog-content"
      >
        <FacilityDialogContentSwitcher
          mode={dialogBodyMode}
          isEdit={isEdit}
          submitting={submitting}
          onClose={() => onOpenChange(false)}
          formContent={
            <div className="facility-create-edit-dialog-form-shell">
              <Formik<FacilityFormValues>
                enableReinitialize
                key={
                  isEdit && facilityId != null ? `edit-${String(facilityId)}` : 'create-facility'
                }
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={submit}
                validateOnBlur
                validateOnChange={false}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  setFieldTouched,
                  setTouched,
                  setFieldError,
                }) => {
                  const err = (k: keyof FacilityFormValues) =>
                    resolveFieldError(
                      k,
                      values,
                      errors as Record<string, unknown>,
                      touched as Record<string, unknown>,
                      FACILITY_DETAILS_KEYS,
                      facilityDetailsSchema,
                      schema
                    );

                  const trimBlur =
                    (field: keyof FacilityFormValues) =>
                    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      handleBlur(e);
                      if (field === 'password') {
                        return;
                      }
                      const raw = e.target.value;
                      if (field === 'code') {
                        const norm = normalizeFacilityCodeInput(raw);
                        if (norm !== values.code) {
                          void setFieldValue('code', norm);
                        }
                        return;
                      }
                      const trimmed = raw.trim();
                      if (trimmed !== raw) {
                        void setFieldValue(field, trimmed);
                      }
                    };

                  let facilityDetailsValid = false;
                  try {
                    facilityDetailsValid = facilityDetailsSchema.isValidSync(values);
                  } catch {
                    facilityDetailsValid = false;
                  }

                  const goToAdminTab = async () => {
                    await validateAndGoToAdminTab({
                      values,
                      touched: touched as Record<string, boolean>,
                      setTouched: setTouched as (
                        t: Record<string, boolean>,
                        validate?: boolean
                      ) => Promise<void> | void,
                      setFieldError: setFieldError as (field: string, message?: string) => void,
                      facilityDetailsKeys: FACILITY_DETAILS_KEYS,
                      facilityDetailsSchema,
                      setTab,
                    });
                  };

                  return (
                    <form
                      onSubmit={e => {
                        if (tab === 'facility') {
                          e.preventDefault();
                          void goToAdminTab();
                          return;
                        }
                        handleSubmit(e);
                      }}
                      className="facility-dialog-form"
                    >
                      <div className="facility-create-edit-dialog-header-with-tabs">
                        <div className="facility-create-edit-dialog-header-top-row">
                          <DialogHeader className="facility-create-edit-dialog-header-title-only">
                            <DialogTitle className="create-user-dialog-title">
                              {isEdit ? 'Edit Facility' : 'Create New Facility'}
                            </DialogTitle>
                          </DialogHeader>
                          <FacilityCreateEditDialogCloseButton
                            disabled={submitting}
                            onClose={() => onOpenChange(false)}
                          />
                        </div>
                        <div
                          className="facility-dialog-tabs"
                          role="tablist"
                          aria-label="Facility form sections"
                        >
                          <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'facility'}
                            className={`facility-dialog-tab ${tab === 'facility' ? 'facility-dialog-tab--active' : ''}`}
                            onClick={() => setTab('facility')}
                          >
                            Facility Details
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'admin'}
                            className={`facility-dialog-tab ${tab === 'admin' ? 'facility-dialog-tab--active' : ''}`}
                            disabled={!facilityDetailsValid}
                            title={
                              facilityDetailsValid
                                ? undefined
                                : 'Complete Facility Details to continue'
                            }
                            onClick={() => facilityDetailsValid && setTab('admin')}
                          >
                            Admin User
                          </button>
                        </div>
                      </div>
                      <FacilityDialogLocationMatch
                        stateOptions={stateOptions}
                        cityOptions={cityOptions}
                        dialogOpen={open}
                      />
                      <FacilityDialogCityFetcher
                        dialogOpen={open}
                        setCityOptions={setCityOptions}
                        setIsLoadingCities={setIsLoadingCities}
                        skipNextCityFetchRef={skipNextCityFetchRef}
                      />
                      <FacilityDialogPincodeLookup
                        dialogOpen={open}
                        stateOptions={stateOptions}
                        setCityOptions={setCityOptions}
                        setIsLoadingCities={setIsLoadingCities}
                        setIsLoadingPincode={setIsLoadingPincode}
                        skipNextCityFetchRef={skipNextCityFetchRef}
                      />
                      <div className="create-user-dialog-body facility-dialog-body facility-create-edit-dialog-scroll-body">
                        {tab === 'facility' ? (
                          <FacilityDetailsTabFields
                            values={values}
                            handleChange={handleChange}
                            trimBlur={trimBlur}
                            err={err}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            stateOptions={stateOptions}
                            cityOptions={cityOptions}
                            isLoadingStates={isLoadingStates}
                            isLoadingCities={isLoadingCities}
                            hubFacilityOptions={hubFacilityOptions}
                            setHubFacilityOptions={setHubFacilityOptions}
                            isLoadingHubFacilities={isLoadingHubFacilities}
                            setIsLoadingHubFacilities={setIsLoadingHubFacilities}
                            isLoadingPincode={isLoadingPincode}
                            isEdit={isEdit}
                            facilityId={facilityId}
                            open={open}
                          />
                        ) : (
                          <AdminUserTabFields
                            values={values}
                            handleChange={handleChange}
                            trimBlur={trimBlur}
                            err={err}
                            isEdit={isEdit}
                            setFieldValue={setFieldValue}
                          />
                        )}
                      </div>

                      <div className="create-user-dialog-footer facility-dialog-footer">
                        {tab === 'facility' ? (
                          <button
                            type="button"
                            className="facility-dialog-btn facility-dialog-btn--primary"
                            disabled={submitting}
                            onClick={() => void goToAdminTab()}
                          >
                            Next
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="facility-dialog-btn"
                              disabled={submitting}
                              onClick={() => setTab('facility')}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="facility-dialog-btn facility-dialog-btn--primary"
                              disabled={submitting}
                            >
                              {facilityDialogSubmitLabel(submitting, isEdit)}
                            </button>
                          </>
                        )}
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
